import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertOctagon, Loader2, ShieldAlert, ExternalLink, RefreshCw, FileSearch, X, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import TickerSearchInput from '@/components/TickerSearchInput';
import ModalCloseButton from '@/components/ModalCloseButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminMarkLiquidatedButton — Feb 2026.
 *
 * Companion to AdminBlocklistTickerButton. The blocklist button
 * PURGES a ticker from stock_cache; this button KEEPS the ticker
 * visible and attaches liquidation metadata so users see a
 * "Liquidating in N days" countdown pill + a red "Liquidated"
 * badge + a link to the prospectus PDF.
 *
 * Use case: SEC 497 prospectus supplements announce liquidations
 * BEFORE the exchange formally delists (see GMO BCHI: supplement
 * filed Jul 31, last trading Aug 14, final liquidation Aug 19).
 * The Massive auto-check won't confirm until AFTER the exchange
 * delists, so the admin pre-flags the fund manually here.
 *
 * POST /api/admin/liquidation/mark/{ticker} with:
 *   source_url          — PDF link  (optional)
 *   last_trading_date   — YYYY-MM-DD (drives countdown)
 *   delisted_utc        — YYYY-MM-DD (final liquidation)
 *   note                — free-form admin note
 */
export default function AdminMarkLiquidatedButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    source_url: '',
    last_trading_date: '',
    delisted_utc: '',
    note: '',
  });
  // Feb 2026 — SEC 497 candidates state. Loaded lazily when the
  // modal opens so we don't hit the endpoint on every dashboard
  // render for admin users.
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  // Feb 2026 — Ripcord retro-scan state (hooks must live above any
  // conditional early return, so declare them up here alongside the
  // other component-level state).
  const [retroBusy, setRetroBusy] = useState(false);
  const [retroPreview, setRetroPreview] = useState(null);
  // Two-click confirm state for "Clear all candidates".
  const [clearConfirm, setClearConfirm] = useState(false);
  // Feb 2026 — Per-ticker blocklist confirm state. Map of ticker →
  // timestamp of first click; expires after 4 seconds so a stray
  // click can't permanently nuke a row.
  const [blocklistConfirm, setBlocklistConfirm] = useState({});
  // Feb 2026 — Recent purges log (component-local, capped at 5).
  // Each entry: { ticker, at, blocklist_size }.
  const [recentPurges, setRecentPurges] = useState([]);
  const [bulkTrashBusy, setBulkTrashBusy] = useState(false);
  const [bulkTrashConfirm, setBulkTrashConfirm] = useState(false);

  // Feb 2026 — Heartbeat while the modal is open so the nightly
  // auto-prune cron knows to skip. 30s cadence is well under the
  // 120s freshness window the server enforces.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const ping = async () => {
      try {
        await axios.post(
          `${API}/admin/liquidation-candidates/heartbeat`,
          {},
          { withCredentials: true },
        );
      } catch { /* silent — heartbeat failure is non-fatal */ }
    };
    ping();
    const t = setInterval(() => { if (!cancelled) ping(); }, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [open]);

  // Fetch the pending candidate list the first time the modal opens.
  useEffect(() => {
    if (open && candidates.length === 0 && !candidatesLoading) {
      loadCandidates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (user?.role !== 'admin') return null;

  const reset = () => {
    setSelected(null);
    setForm({ source_url: '', last_trading_date: '', delisted_utc: '', note: '' });
  };

  const loadCandidates = async () => {
    setCandidatesLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/liquidation-candidates?status=pending&limit=50`,
        { withCredentials: true },
      );
      setCandidates(data?.candidates || []);
    } catch (err) {
      toast.error('Failed to load SEC candidates', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setCandidatesLoading(false);
    }
  };

  const runScanNow = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      // 31-day lookback on manual scan. Scan is now BACKGROUND on
      // the server (returns immediately) because a 31-day sweep
      // across 7 keywords + per-filing text fetches routinely runs
      // 60-90s — longer than the Cloudflare/ingress timeout. We
      // show a "queued" toast and auto-refresh the candidates list
      // after 90 seconds.
      const { data } = await axios.post(
        `${API}/admin/liquidation-candidates/scan-now?days_back=31`,
        {},
        { withCredentials: true },
      );
      toast.info('EDGAR 497 scan queued', {
        description: data?.message || 'Refreshing candidates in ~90 seconds…',
        duration: 90000,
      });
      // Poll for completion: refresh at 30s, 60s, 90s so early
      // hits show up as they land.
      setTimeout(() => { loadCandidates(); }, 30000);
      setTimeout(() => { loadCandidates(); }, 60000);
      setTimeout(() => {
        loadCandidates();
        toast.success('Candidates list refreshed');
      }, 90000);
    } catch (err) {
      toast.error('Scan failed', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      // Release the button immediately — the scan is running in
      // the background, no need to keep it spinning for 90s.
      setScanning(false);
    }
  };

  const dismissCandidate = async (accession) => {
    try {
      await axios.post(
        `${API}/admin/liquidation-candidates/${accession}/dismiss`,
        {},
        { withCredentials: true },
      );
      setCandidates((prev) => prev.filter((c) => c.accession_number !== accession));
      toast.success('Candidate dismissed');
    } catch (err) {
      toast.error('Failed to dismiss', {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  // Feb 2026 — Two-click blocklist. First click arms the button
  // (turns solid red); second click within 4 seconds actually hits
  // the existing /admin/blocklist/{ticker} endpoint which purges
  // the ticker from stock_cache (dividend AND non-dividend paths)
  // and adds it to dynamic_blocklist so it can never be re-cached.
  const blocklistTicker = async (ticker, accession) => {
    const now = Date.now();
    const armedAt = blocklistConfirm[ticker];
    if (!armedAt || now - armedAt > 4000) {
      setBlocklistConfirm((prev) => ({ ...prev, [ticker]: now }));
      setTimeout(() => {
        setBlocklistConfirm((prev) => {
          if (prev[ticker] === now) {
            const { [ticker]: _drop, ...rest } = prev;
            return rest;
          }
          return prev;
        });
      }, 4000);
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/admin/blocklist/${ticker}`,
        { reason: `Liquidated fund purge via candidate ${accession || 'manual'}` },
        { withCredentials: true },
      );
      const removed = data?.removed || {};
      const summary = Object.entries(removed)
        .filter(([, n]) => Number(n) > 0)
        .map(([k, n]) => `${k}: ${n}`)
        .join(', ') || 'no cached rows';
      // Feb 2026 — Undo toast. 30-second window; clicking Undo hits
      // DELETE /admin/blocklist/{ticker} which removes the ticker
      // from both `dynamic_blocklist` and the in-memory
      // `TICKER_BLOCKLIST` set. Any PURGED cache rows are gone for
      // good — Undo only lets Massive re-cache the ticker on next
      // Stock Modal open.
      toast.success(`${ticker} purged permanently`, {
        description: `Removed (${summary}). Blocklist size ${data?.blocklist_size}.`,
        duration: 30000,
        action: {
          label: 'Undo',
          onClick: () => { unblocklistTicker(ticker); },
        },
      });
      // Recent purges log (cap 5, newest first, dedup by ticker).
      setRecentPurges((prev) => {
        const next = [{ ticker, at: Date.now(), blocklist_size: data?.blocklist_size }, ...prev.filter((p) => p.ticker !== ticker)];
        return next.slice(0, 5);
      });
      setBlocklistConfirm((prev) => {
        const { [ticker]: _drop, ...rest } = prev;
        return rest;
      });
      if (accession) {
        try {
          await axios.post(
            `${API}/admin/liquidation-candidates/${accession}/dismiss`,
            {},
            { withCredentials: true },
          );
        } catch { /* non-fatal */ }
        setCandidates((prev) => prev.filter((c) => c.accession_number !== accession));
      }
      return true;
    } catch (err) {
      toast.error(`Failed to blocklist ${ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
      return false;
    }
  };

  // Feb 2026 — Un-blocklist. Reverses a POST /admin/blocklist call.
  // Used by the Undo toast action AND the "un-blocklist" link in
  // the Recent Purges panel below.
  const unblocklistTicker = async (ticker) => {
    try {
      const { data } = await axios.delete(
        `${API}/admin/blocklist/${ticker}`,
        { withCredentials: true },
      );
      toast.success(`${ticker} restored`, {
        description: `Removed from blocklist. Blocklist size ${data?.blocklist_size}. Any purged cache rows will re-populate on the next Stock Modal open.`,
      });
      setRecentPurges((prev) => prev.filter((p) => p.ticker !== ticker));
    } catch (err) {
      toast.error(`Failed to restore ${ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  // Feb 2026 — Bulk trash. Walks every pending candidate whose
  // extracted liquidation date is already in the past (i.e. the
  // rows with the red dot) and blocklists each ticker in sequence.
  // Two-click confirm on the button; 200ms delay between purges to
  // keep the backend calm. No per-purge undo toast (the summary
  // toast at the end covers all of them).
  const bulkTrashPastLiquidations = async () => {
    if (bulkTrashBusy) return;
    if (!bulkTrashConfirm) {
      setBulkTrashConfirm(true);
      setTimeout(() => setBulkTrashConfirm(false), 4000);
      return;
    }
    setBulkTrashBusy(true);
    setBulkTrashConfirm(false);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Candidates with a real ticker AND a past liquidation date.
    const targets = candidates.filter((c) => {
      const t = (c.tickers && c.tickers[0]);
      if (!t) return false;
      const liq = c.extracted_liquidation_date ? new Date(c.extracted_liquidation_date) : null;
      const ltd = c.extracted_last_trading_date ? new Date(c.extracted_last_trading_date) : null;
      return (liq && !Number.isNaN(liq.getTime()) && liq < today)
          || (ltd && !Number.isNaN(ltd.getTime()) && ltd < today);
    });
    if (targets.length === 0) {
      toast.info('No past-liquidation candidates to purge.');
      setBulkTrashBusy(false);
      return;
    }
    let ok = 0;
    const purgedTickers = [];
    for (const c of targets) {
      // Iterate every ticker on the row (multi-ticker filings).
      for (const t of (c.tickers || [])) {
        try {
          await axios.post(
            `${API}/admin/blocklist/${t}`,
            { reason: `Bulk purge: past-liquidation candidate ${c.accession_number}` },
            { withCredentials: true },
          );
          ok += 1;
          purgedTickers.push(t);
        } catch { /* keep going */ }
        await new Promise((r) => setTimeout(r, 200));
      }
      try {
        await axios.post(
          `${API}/admin/liquidation-candidates/${c.accession_number}/dismiss`,
          {},
          { withCredentials: true },
        );
      } catch { /* non-fatal */ }
    }
    setCandidates((prev) => prev.filter((c) => !targets.some((t) => t.accession_number === c.accession_number)));
    // Push a single combined entry into Recent Purges so all of
    // them are one Undo-able unit (Undo clicks each in sequence).
    if (purgedTickers.length > 0) {
      setRecentPurges((prev) => {
        const bulkEntry = { ticker: `${purgedTickers.length} tickers (bulk)`, at: Date.now(), tickers: purgedTickers };
        return [bulkEntry, ...prev].slice(0, 5);
      });
    }
    toast.success(`Purged ${ok} ticker${ok === 1 ? '' : 's'}`, {
      description: purgedTickers.slice(0, 8).join(', ') + (purgedTickers.length > 8 ? ` + ${purgedTickers.length - 8} more` : ''),
      duration: 30000,
      action: purgedTickers.length > 0 ? {
        label: 'Undo all',
        onClick: async () => {
          for (const t of purgedTickers) {
            try {
              await axios.delete(`${API}/admin/blocklist/${t}`, { withCredentials: true });
            } catch { /* keep going */ }
          }
          toast.success(`Restored ${purgedTickers.length} ticker${purgedTickers.length === 1 ? '' : 's'} from blocklist`);
          setRecentPurges((prev) => prev.filter((p) => p.tickers !== purgedTickers));
        },
      } : undefined,
    });
    setBulkTrashBusy(false);
  };


  // Feb 2026 — Nuke the entire candidates list. Two-click confirm
  // so a stray click doesn't lose review state. The cron refills
  // it on the next 7 AM run (or click Scan now immediately).
  const clearAllCandidates = async () => {
    if (!clearConfirm) { setClearConfirm(true); setTimeout(() => setClearConfirm(false), 4000); return; }
    try {
      const { data } = await axios.delete(
        `${API}/admin/liquidation-candidates`,
        { withCredentials: true },
      );
      setCandidates([]);
      setClearConfirm(false);
      toast.success(`Cleared ${data.deleted} candidate${data.deleted === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error('Failed to clear', {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  // Auto-fill the form from a SEC candidate. Ticker comes from the
  // filing's `tickers` array (first entry). Filing URL becomes the
  // Source URL. The display_name + matched phrase get pre-populated
  // in the Note so admin has context ready.
  // Feb 2026 — Ripcord retro-scan. Two-stage: preview → confirm.
  const runRetroPreview = async () => {
    if (retroBusy) return;
    setRetroBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/dividend-ripcord-retro-scan?commit=false`,
        {},
        { withCredentials: true },
      );
      setRetroPreview(data);
      toast.info(`Retro-scan preview: ${data.would_flag} row${data.would_flag === 1 ? '' : 's'} would be reclassified`);
    } catch (err) {
      toast.error('Retro-scan preview failed', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setRetroBusy(false);
    }
  };
  const runRetroCommit = async () => {
    if (retroBusy) return;
    setRetroBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/dividend-ripcord-retro-scan?commit=true`,
        {},
        { withCredentials: true },
      );
      toast.success(`Retro-scan applied: ${data.flagged} row${data.flagged === 1 ? '' : 's'} reclassified as special`);
      setRetroPreview(null);
    } catch (err) {
      toast.error('Retro-scan commit failed', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setRetroBusy(false);
    }
  };

  const applyCandidate = async (cand) => {
    const tkr = (cand.tickers && cand.tickers[0]) || null;
    if (!tkr) {
      toast.error('No ticker on this filing — cannot auto-apply. Enter manually.');
      return;
    }
    // Fetch the ticker preview like the search path does.
    try {
      const { data } = await axios.get(`${API}/stocks/${tkr}`, { withCredentials: true });
      setSelected({
        ticker: data.ticker || tkr,
        name: data.name || data.company_name || tkr,
        exchange: data.exchange,
      });
      setForm({
        source_url: cand.filing_url || data.liquidation_source_url || '',
        last_trading_date: cand.extracted_last_trading_date
          || (data.liquidation_last_trading_date || '').slice(0, 10),
        delisted_utc: cand.extracted_liquidation_date
          || (data.delisted_utc || '').slice(0, 10),
        note: data.liquidation_note
          || `SEC 497 filing ${cand.accession_number} (${cand.file_date}) matched: ${cand.matched_query}.${cand.extracted_last_trading_date || cand.extracted_liquidation_date ? ' Dates auto-extracted from filing — verify against the source PDF.' : ' Review the filing and fill in last trading day + final liquidation date.'}`,
      });
      const autofilled = [
        cand.extracted_last_trading_date && 'last trading day',
        cand.extracted_liquidation_date && 'liquidation date',
      ].filter(Boolean);
      toast.info(
        `Loaded ${tkr} from SEC filing`,
        autofilled.length
          ? { description: `Auto-filled: ${autofilled.join(', ')}. Verify against the PDF then Save.` }
          : { description: 'Fill in dates from the source PDF then Save.' }
      );
    } catch (err) {
      toast.error(`Could not load ${tkr}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  const handleSelect = async (ticker) => {
    if (!ticker) return;
    const upper = String(ticker).toUpperCase();
    try {
      const { data } = await axios.get(`${API}/stocks/${upper}`, { withCredentials: true });
      setSelected({
        ticker: data.ticker || upper,
        name: data.name || data.company_name || upper,
        exchange: data.exchange,
        // Pre-populate form from existing DB values so we can EDIT
        // (not just SET) — admin can update a source URL without
        // clobbering the last-trading date.
      });
      setForm({
        source_url: data.liquidation_source_url || '',
        last_trading_date: (data.liquidation_last_trading_date || '').slice(0, 10),
        delisted_utc: (data.delisted_utc || '').slice(0, 10),
        note: data.liquidation_note || '',
      });
    } catch (err) {
      toast.error(`Could not load ${upper}`, {
        description: err?.response?.data?.detail || err?.message,
      });
      setSelected({ ticker: upper, name: upper });
    }
  };

  const submit = async () => {
    if (!selected?.ticker || busy) return;
    const body = {
      source_url: form.source_url.trim() || undefined,
      last_trading_date: form.last_trading_date.trim() || undefined,
      delisted_utc: form.delisted_utc.trim() || undefined,
      note: form.note.trim() || undefined,
    };
    if (!body.source_url && !body.last_trading_date && !body.delisted_utc && !body.note) {
      toast.error('Fill at least one field before saving.');
      return;
    }
    setBusy(true);
    try {
      const { data: _data } = await axios.post(
        `${API}/admin/liquidation/mark/${selected.ticker}`,
        body,
        { withCredentials: true },
      );
      toast.success(`${selected.ticker} marked liquidated`, {
        description: `Fields updated: ${Object.keys(body).filter((k) => body[k]).join(', ')}.`,
      });
      // Keep the modal open so admin can immediately verify the
      // preview via the "View saved fields" link (below) if they
      // want, but reset the form.
      setSelected(null);
      setForm({ source_url: '', last_trading_date: '', delisted_utc: '', note: '' });
    } catch (err) {
      toast.error(`Failed to mark ${selected.ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white transition-colors"
        data-testid="admin-mark-liquidated-btn"
        title="Attach liquidation metadata (source PDF, last trading day, note) to a ticker without purging it."
      >
        <AlertOctagon className="w-3.5 h-3.5" />
        Mark Liquidated
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
          onClick={() => { setOpen(false); reset(); }}
          data-testid="admin-mark-liquidated-modal"
        >
          <div
            className="bg-card border rounded-xl shadow-2xl w-full max-w-3xl relative max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
              <h3 className="text-base font-bold flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-amber-500" />
                Mark Liquidated (preserve ticker)
              </h3>
              <ModalCloseButton onClick={() => { setOpen(false); reset(); }} testId="admin-mark-liquidated-close" />
            </div>

            <div className="p-4 overflow-y-auto space-y-4">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Attach liquidation metadata to a ticker without removing it
                from the database. The ticker stays searchable, but every
                Stock Modal + row shows a countdown badge (&ldquo;Liquidating
                in N days&rdquo;) and a link to the source PDF. Use this for
                SEC 497 prospectus supplements filed BEFORE the exchange
                formally delists.
              </p>

              {/* Feb 2026 — SEC 497 Candidates panel. Runs the nightly
                  cron on-demand and surfaces every unresolved filing
                  the scanner flagged. Click a candidate to pre-fill
                  the form with the filing URL + ticker; click the X
                  to dismiss a false positive. */}
              <div className="rounded-lg border border-blue-200/60 dark:border-blue-800/40 bg-blue-50/40 dark:bg-blue-950/20 p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5">
                    <FileSearch className="w-4 h-4 text-blue-700 dark:text-blue-300" />
                    <span className="text-xs font-bold uppercase tracking-wider text-blue-800 dark:text-blue-200">
                      SEC 497 Candidates
                    </span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-800 dark:text-blue-200">
                      {candidatesLoading ? '…' : candidates.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    {(() => {
                      // Feb 2026 — Count how many candidates have a
                      // past liquidation date (red-dot rows). Bulk
                      // Trash button is enabled only when there's
                      // at least one to purge.
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const staleCount = candidates.filter((c) => {
                        const t = (c.tickers && c.tickers[0]);
                        if (!t) return false;
                        const liq = c.extracted_liquidation_date ? new Date(c.extracted_liquidation_date) : null;
                        const ltd = c.extracted_last_trading_date ? new Date(c.extracted_last_trading_date) : null;
                        return (liq && !Number.isNaN(liq.getTime()) && liq < today)
                            || (ltd && !Number.isNaN(ltd.getTime()) && ltd < today);
                      }).length;
                      if (staleCount === 0) return null;
                      return (
                        <button
                          type="button"
                          onClick={bulkTrashPastLiquidations}
                          disabled={bulkTrashBusy}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold transition-colors disabled:opacity-50 ${
                            bulkTrashConfirm
                              ? 'bg-rose-700 hover:bg-rose-800 text-white ring-2 ring-rose-400/60'
                              : 'bg-rose-600 hover:bg-rose-700 text-white'
                          }`}
                          data-testid="admin-mark-liquidated-bulk-trash"
                          title={bulkTrashConfirm
                            ? `Click once more to purge all ${staleCount} past-liquidation tickers.`
                            : `Purge all ${staleCount} tickers whose liquidation date has already passed. Two-click confirm.`}
                        >
                          {bulkTrashBusy
                            ? <><Loader2 className="w-3 h-3 animate-spin" /> Purging…</>
                            : <><Trash2 className="w-3 h-3" /> {bulkTrashConfirm ? `Confirm ${staleCount}` : `Purge past (${staleCount})`}</>}
                        </button>
                      );
                    })()}
                    <button
                      type="button"
                      onClick={clearAllCandidates}
                      disabled={candidates.length === 0}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        clearConfirm
                          ? 'bg-rose-700 hover:bg-rose-800 text-white ring-2 ring-rose-500/60'
                          : 'bg-background border border-border hover:bg-muted text-foreground'
                      }`}
                      data-testid="admin-mark-liquidated-clear"
                      title={clearConfirm
                        ? 'Click once more to permanently delete every candidate row.'
                        : 'Wipe the candidates list so you can rerun the scan with a different window.'}
                    >
                      <Trash2 className="w-3 h-3" />
                      {clearConfirm ? 'Confirm clear' : 'Clear all'}
                    </button>
                    <button
                      type="button"
                      onClick={runScanNow}
                      disabled={scanning}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
                      data-testid="admin-mark-liquidated-scan-now"
                      title="Run the EDGAR 497 full-text scan over the last 31 days"
                    >
                      {scanning
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Scanning…</>
                        : <><RefreshCw className="w-3 h-3" /> Scan now</>}
                    </button>
                  </div>
                </div>
                {candidatesLoading ? (
                  <div className="text-[11px] text-muted-foreground py-2 flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading pending candidates…
                  </div>
                ) : candidates.length === 0 ? (
                  <div className="text-[11px] text-muted-foreground italic py-2">
                    No pending SEC 497 filings flagged. Nightly scan runs at 7 AM ET, or click &quot;Scan now&quot;.
                  </div>
                ) : (
                  <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                    {candidates.map((c) => {
                      // Feb 2026 — Red-dot logic. If the extracted
                      // last-trading OR liquidation date has already
                      // passed we flag the filing as "already done"
                      // so admin can see at a glance that the
                      // ticker is post-liquidation. Compare against
                      // today at midnight to avoid TZ noise.
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const ltd = c.extracted_last_trading_date ? new Date(c.extracted_last_trading_date) : null;
                      const liq = c.extracted_liquidation_date ? new Date(c.extracted_liquidation_date) : null;
                      const stale = (ltd && !Number.isNaN(ltd.getTime()) && ltd < today)
                        || (liq && !Number.isNaN(liq.getTime()) && liq < today);
                      return (
                      <li
                        key={c.accession_number}
                        className="flex items-start gap-2 rounded-md border border-border/40 bg-background/80 px-2 py-1.5 text-[11px]"
                        data-testid={`admin-mark-liquidated-candidate-${c.accession_number}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-foreground flex items-center gap-1.5 flex-wrap">
                            {stale && (
                              <span
                                className="inline-block w-2 h-2 rounded-full bg-rose-500 shrink-0 shadow-[0_0_4px_rgba(244,63,94,0.5)]"
                                title="This filing's liquidation date has already passed — action likely complete."
                                data-testid={`admin-mark-liquidated-candidate-stale-${c.accession_number}`}
                              />
                            )}
                            {(c.tickers && c.tickers.length > 0) ? (
                              <span className="inline-flex items-center gap-1 flex-wrap">
                                {c.tickers.map((t) => {
                                  const armed = !!blocklistConfirm[t];
                                  return (
                                    <span key={t} className="inline-flex items-center gap-0.5">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          blocklistTicker(t, c.accession_number);
                                        }}
                                        className={`inline-flex items-center p-0.5 rounded transition-colors ${
                                          armed
                                            ? 'bg-rose-600 text-white ring-2 ring-rose-400 shadow'
                                            : 'text-muted-foreground hover:text-rose-600 hover:bg-rose-500/10'
                                        }`}
                                        title={armed
                                          ? `Click again within 4 seconds to PERMANENTLY purge ${t} from every cache and blocklist it forever.`
                                          : `Purge ${t} from the database and blocklist it (2-click confirm).`}
                                        data-testid={`admin-mark-liquidated-blocklist-${t}`}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.dispatchEvent(new CustomEvent('open-stock-modal', {
                                            detail: { ticker: String(t).toUpperCase() },
                                          }));
                                        }}
                                        className="font-mono text-foreground hover:text-blue-700 dark:hover:text-blue-300 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500/40 rounded-sm"
                                        title={`Open ${t} in the Stock Modal (this admin modal stays open)`}
                                        data-testid={`admin-mark-liquidated-candidate-open-${t}`}
                                      >
                                        {t}
                                      </button>
                                    </span>
                                  );
                                })}
                              </span>
                            ) : (c.possible_tickers && c.possible_tickers.length > 0) ? (
                              <span
                                className="inline-flex items-center gap-1 flex-wrap"
                                title="Ticker unclear — trust CIK has multiple ETFs. Click any to review."
                              >
                                <span className="text-[9px] uppercase tracking-wider text-amber-600 dark:text-amber-400 font-bold shrink-0">
                                  Possible:
                                </span>
                                {c.possible_tickers.slice(0, 6).map((t) => {
                                  const armed = !!blocklistConfirm[t];
                                  return (
                                    <span key={t} className="inline-flex items-center gap-0.5">
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          blocklistTicker(t, c.accession_number);
                                        }}
                                        className={`inline-flex items-center p-0.5 rounded transition-colors ${
                                          armed
                                            ? 'bg-rose-600 text-white ring-2 ring-rose-400 shadow'
                                            : 'text-muted-foreground hover:text-rose-600 hover:bg-rose-500/10'
                                        }`}
                                        title={armed
                                          ? `Click again within 4 seconds to PERMANENTLY purge ${t}.`
                                          : `Purge ${t} from the database and blocklist it (2-click confirm).`}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.dispatchEvent(new CustomEvent('open-stock-modal', {
                                            detail: { ticker: String(t).toUpperCase() },
                                          }));
                                        }}
                                        className="font-mono text-amber-700 dark:text-amber-300 hover:underline focus:outline-none focus:ring-2 focus:ring-amber-500/40 rounded-sm"
                                      >
                                        {t}
                                      </button>
                                    </span>
                                  );
                                })}
                                {c.possible_tickers.length > 6 && (
                                  <span className="text-muted-foreground">+{c.possible_tickers.length - 6}</span>
                                )}
                              </span>
                            ) : (
                              <span className="italic text-muted-foreground">(no ticker)</span>
                            )}
                            {/* Feb 2026 — Extracted dates inline. Hover
                                each date to see the raw source phrase
                                the regex matched — quick sanity check
                                before you commit. */}
                            {(c.extracted_last_trading_date || c.extracted_liquidation_date) && (
                              <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                                {c.extracted_last_trading_date && (
                                  <span title={c.extracted_last_trading_source ? `Source: …${c.extracted_last_trading_source}…` : undefined}>
                                    Last <span className="text-foreground">{c.extracted_last_trading_date.slice(5).replace('-','/')}</span>
                                  </span>
                                )}
                                {c.extracted_last_trading_date && c.extracted_liquidation_date && ' · '}
                                {c.extracted_liquidation_date && (
                                  <span title={c.extracted_liquidation_source ? `Source: …${c.extracted_liquidation_source}…` : undefined}>
                                    Liq <span className="text-foreground">{c.extracted_liquidation_date.slice(5).replace('-','/')}</span>
                                  </span>
                                )}
                              </span>
                            )}
                            {c.display_names && c.display_names[0] && (
                              <span className="font-normal text-muted-foreground">
                                {c.display_names[0]}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-muted-foreground font-mono">
                            {c.file_date} · {c.accession_number} · matched {c.matched_query}
                          </div>
                        </div>
                        {c.filing_url && (
                          <a
                            href={c.filing_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-700 dark:text-blue-300 underline hover:no-underline shrink-0"
                            title="Open the filing index on SEC.gov"
                          >
                            <ExternalLink className="w-2.5 h-2.5" /> View
                          </a>
                        )}
                        <button
                          type="button"
                          onClick={() => applyCandidate(c)}
                          disabled={!(c.tickers && c.tickers[0])}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                          data-testid={`admin-mark-liquidated-candidate-apply-${c.accession_number}`}
                          title={!(c.tickers && c.tickers[0]) ? 'No ticker on filing — enter manually' : 'Pre-fill the form with this filing'}
                        >
                          Load
                        </button>
                        <button
                          type="button"
                          onClick={() => dismissCandidate(c.accession_number)}
                          className="inline-flex items-center gap-0.5 p-0.5 rounded text-muted-foreground hover:text-rose-600 hover:bg-rose-500/10 transition-colors shrink-0"
                          data-testid={`admin-mark-liquidated-candidate-dismiss-${c.accession_number}`}
                          title="Dismiss — false positive or already handled"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </li>
                      );
                    })}
                  </ul>
                )}
                {/* Feb 2026 — Recent Purges log. Component-local
                    (resets when modal closes) — server-side history
                    would need a new collection. Keeps the last 5
                    tickers you nuked with one-click un-blocklist so
                    you can bail out of a mistake even after the
                    Undo toast expires. */}
                {recentPurges.length > 0 && (
                  <div className="mt-3 pt-2 border-t border-blue-200/60 dark:border-blue-800/40">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-blue-800 dark:text-blue-200 mb-1">
                      Recent Purges
                    </div>
                    <ul className="space-y-0.5">
                      {recentPurges.map((p, i) => (
                        <li
                          key={`${p.ticker}-${p.at}`}
                          className="flex items-center gap-2 text-[10px] font-mono"
                          data-testid={`admin-mark-liquidated-recent-purge-${i}`}
                        >
                          <span className="text-foreground truncate flex-1">{p.ticker}</span>
                          <span className="text-muted-foreground shrink-0">
                            {new Date(p.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              if (p.tickers && p.tickers.length > 0) {
                                // Bulk entry — restore each in sequence.
                                (async () => {
                                  for (const t of p.tickers) {
                                    try {
                                      await axios.delete(`${API}/admin/blocklist/${t}`, { withCredentials: true });
                                    } catch { /* keep going */ }
                                  }
                                  toast.success(`Restored ${p.tickers.length} ticker${p.tickers.length === 1 ? '' : 's'}`);
                                  setRecentPurges((prev) => prev.filter((x) => x.at !== p.at));
                                })();
                              } else {
                                unblocklistTicker(p.ticker);
                              }
                            }}
                            className="text-blue-700 dark:text-blue-300 hover:underline shrink-0"
                            title="Remove this ticker from the blocklist (cache rows are still gone; Massive will re-cache on next Stock Modal open)"
                            data-testid={`admin-mark-liquidated-recent-purge-undo-${i}`}
                          >
                            un-blocklist
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Feb 2026 — Ripcord retro-scan panel. Two-stage flow:
                  "Preview" builds a diff list without touching data;
                  "Apply" commits. Costco-style planned specials that
                  Massive already tagged `special` upstream are
                  auto-skipped by the backend so we don't downgrade
                  legitimately-recurring quarterly dividends. */}
              <div className="rounded-lg border border-purple-200/60 dark:border-purple-800/40 bg-purple-50/40 dark:bg-purple-950/20 p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-1.5">
                    <RefreshCw className="w-4 h-4 text-purple-700 dark:text-purple-300" />
                    <span className="text-xs font-bold uppercase tracking-wider text-purple-800 dark:text-purple-200">
                      Ripcord Retro-Scan
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={runRetroPreview}
                      disabled={retroBusy}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-purple-600 hover:bg-purple-700 text-white transition-colors disabled:opacity-50"
                      data-testid="admin-ripcord-retro-preview"
                    >
                      {retroBusy && !retroPreview
                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Scanning…</>
                        : <>Preview</>}
                    </button>
                    {retroPreview && retroPreview.would_flag > 0 && (
                      <button
                        type="button"
                        onClick={runRetroCommit}
                        disabled={retroBusy}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold bg-rose-600 hover:bg-rose-700 text-white transition-colors disabled:opacity-50"
                        data-testid="admin-ripcord-retro-commit"
                      >
                        {retroBusy
                          ? <><Loader2 className="w-3 h-3 animate-spin" /> Applying…</>
                          : <>Apply ({retroPreview.would_flag})</>}
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground leading-relaxed mb-1">
                  Re-runs the 5×-trailing-avg heuristic across ALL historical
                  dividend rows and reclassifies outliers as{' '}
                  <code className="font-mono">special</code>. Rows already
                  tagged special by Massive (e.g. Costco&apos;s planned $15
                  payouts) are auto-skipped.
                </p>
                {retroPreview && (
                  <div className="mt-2 text-[10px]">
                    {retroPreview.would_flag === 0 ? (
                      <div className="italic text-muted-foreground">
                        No historical outliers found — data is clean.
                      </div>
                    ) : (
                      <div className="max-h-40 overflow-y-auto rounded border border-border/40 bg-background/60 p-1.5 font-mono space-y-0.5">
                        {(retroPreview.sample || []).map((r) => (
                          <div key={`${r.ticker}-${r.ex_dividend_date}`} className="flex items-center gap-2 justify-between">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.dispatchEvent(new CustomEvent('open-stock-modal', {
                                  detail: { ticker: String(r.ticker).toUpperCase() },
                                }));
                              }}
                              className="font-bold text-foreground shrink-0 w-14 text-left hover:text-purple-700 dark:hover:text-purple-300 hover:underline focus:outline-none focus:ring-2 focus:ring-purple-500/40 rounded-sm"
                              title={`Open ${r.ticker} in the Stock Modal (this admin modal stays open)`}
                              data-testid={`admin-ripcord-retro-row-open-${r.ticker}-${r.ex_dividend_date}`}
                            >
                              {r.ticker}
                            </button>
                            <span className="text-muted-foreground shrink-0 w-20">{r.ex_dividend_date}</span>
                            <span className="text-foreground shrink-0 w-14">${r.cash_amount.toFixed(4)}</span>
                            <span className="text-purple-700 dark:text-purple-300 shrink-0">{r.ratio}× avg</span>
                            <span className="text-muted-foreground shrink-0 truncate">
                              old: {r.old_type || '—'}
                            </span>
                          </div>
                        ))}
                        {retroPreview.would_flag > (retroPreview.sample || []).length && (
                          <div className="text-muted-foreground italic pt-1">
                            …and {retroPreview.would_flag - (retroPreview.sample || []).length} more.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Ticker
                </label>
                <TickerSearchInput
                  onSelect={handleSelect}
                  placeholder="Search ticker or company name…"
                  testid="admin-mark-liquidated-search"
                  maxResults={10}
                  dividendFilter="all"
                />
              </div>

              {selected && (
                <div className="border rounded-lg p-3 bg-muted/30 flex items-baseline gap-2 flex-wrap">
                  <span className="text-lg font-bold font-mono">{selected.ticker}</span>
                  {selected.exchange && (
                    <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {selected.exchange}
                    </span>
                  )}
                  <span className="text-sm text-foreground/80 truncate">{selected.name}</span>
                </div>
              )}

              {selected && (
                <div className="space-y-2.5">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                      Source URL (SEC 497 supplement / press release)
                    </label>
                    <input
                      type="url"
                      value={form.source_url}
                      onChange={(e) => setForm((f) => ({ ...f, source_url: e.target.value.slice(0, 500) }))}
                      placeholder="https://www.gmo.com/globalassets/…/beyond-china-etf_supplement.pdf"
                      className="w-full px-2 py-1.5 rounded-md text-xs bg-background border border-border focus:outline-none focus:ring-2 focus:ring-amber-500/40"
                      data-testid="admin-mark-liquidated-source-url"
                    />
                    {form.source_url && (
                      <a
                        href={form.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 dark:text-amber-300 underline hover:no-underline"
                      >
                        <ExternalLink className="w-2.5 h-2.5" />
                        Open link in a new tab (preview)
                      </a>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                        Last trading day
                      </label>
                      <input
                        type="date"
                        value={form.last_trading_date}
                        onChange={(e) => setForm((f) => ({ ...f, last_trading_date: e.target.value }))}
                        className="w-full px-2 py-1.5 rounded-md text-xs bg-background border border-border focus:outline-none focus:ring-2 focus:ring-amber-500/40 font-mono"
                        data-testid="admin-mark-liquidated-last-trading-date"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                        Final liquidation date
                      </label>
                      <input
                        type="date"
                        value={form.delisted_utc}
                        onChange={(e) => setForm((f) => ({ ...f, delisted_utc: e.target.value }))}
                        className="w-full px-2 py-1.5 rounded-md text-xs bg-background border border-border focus:outline-none focus:ring-2 focus:ring-amber-500/40 font-mono"
                        data-testid="admin-mark-liquidated-delisted-utc"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                      Admin note (surfaced in the Stock Modal banner)
                    </label>
                    <textarea
                      value={form.note}
                      onChange={(e) => setForm((f) => ({ ...f, note: e.target.value.slice(0, 600) }))}
                      placeholder="e.g. GMO ETF Trust supplement dated 2026-07-31: Board voted to liquidate the fund. Last trading day 2026-08-14; final liquidation on or before 2026-08-19."
                      rows={3}
                      className="w-full px-2 py-1.5 rounded-md text-xs bg-background border border-border focus:outline-none focus:ring-2 focus:ring-amber-500/40 resize-none"
                      data-testid="admin-mark-liquidated-note"
                      maxLength={600}
                    />
                    <div className="text-[10px] text-muted-foreground mt-0.5 text-right">
                      {600 - (form.note || '').length} characters left
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t bg-muted/30">
              <button
                type="button"
                onClick={() => { setOpen(false); reset(); }}
                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-background border border-border hover:bg-muted transition-colors"
                data-testid="admin-mark-liquidated-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!selected || busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="admin-mark-liquidated-submit"
              >
                {busy
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
                  : <><AlertOctagon className="w-3.5 h-3.5" /> Save liquidation metadata</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
